/**
 * Running inside the Flashsuite phone app (flashsuite-phone docs/APP-MODE.md): it adds `FlashsuiteApp/<version>`
 * to the user agent of every page it holds. Inside it nothing is sold (Apple 3.1.3(f), Google Play's
 * consumption-only rule), so the page and the API both read the mark: the page to leave out what sells, the API
 * to word its answers without pointing anyone to buy.
 */
const MARK = /\bFlashsuiteApp\/\d+/;

/** Whether this user agent is the Flashsuite phone app's. */
export const isInAppAgent = (userAgent: string | null | undefined): boolean => !!userAgent && MARK.test(userAgent);

/** The phone app's tab a route belongs to, and whether the route is that tab's own screen. */
export interface AppTab {
  id: string;
  /** Where the tab's own screen is. */
  to: string;
  /** The routes that belong to the tab, by their first part. */
  area: string[];
}

/** What a page tells the phone app (APP-MODE.md section 3), sent with `window.ReactNativeWebView.postMessage`. */
export type ToApp =
  | { v: 1; kind: "place"; path: string; tab: string | null; root: boolean }
  | { v: 1; kind: "tab"; tab: string; path: string }
  | { v: 1; kind: "ready" };

/** What the phone app tells a page, as the `detail` of a `flashsuite-app` event on its window. */
export type FromApp = { kind: "tab-again"; tab: string } | { kind: "shown" } | { kind: "hidden" };

/** The app's message, or null for anything else on the event. */
export function fromApp(detail: unknown): FromApp | null {
  if (typeof detail !== "object" || detail === null) return null;
  const d = detail as Record<string, unknown>;
  if (d.kind === "shown") return { kind: "shown" };
  if (d.kind === "hidden") return { kind: "hidden" };
  if (d.kind === "tab-again" && typeof d.tab === "string") return { kind: "tab-again", tab: d.tab };
  return null;
}

const under = (pathname: string, part: string) => pathname === part || pathname.startsWith(`${part}/`);

/** The tab whose area `pathname` is in, or null outside every tab. */
export function tabOf(tabs: readonly AppTab[], pathname: string): AppTab | null {
  return tabs.find((t) => t.area.some((part) => under(pathname, part))) ?? null;
}

/** Where the page is, for the phone app: the route, its tab, and whether it is that tab's own screen. */
export function placeOf(tabs: readonly AppTab[], pathname: string, search: string): ToApp {
  const tab = tabOf(tabs, pathname);
  const path = pathname.replace(/\/+$/, "") || "/";
  return { v: 1, kind: "place", path: pathname + search, tab: tab?.id ?? null, root: !!tab && tab.to === path };
}

/**
 * What a press on `target` in the page's own tab bar does inside the app: a press on another tab asks the app
 * for that tab's screen (and the page stays where it is); a press on the tab the page is in does what it does
 * in a browser. Outside the app every press is the browser's.
 */
export function tabPress(inApp: boolean, tabs: readonly AppTab[], pathname: string, target: AppTab): ToApp | null {
  if (!inApp || tabOf(tabs, pathname)?.id === target.id) return null;
  return { v: 1, kind: "tab", tab: target.id, path: target.to };
}
